import React from 'react'
import { Well } from '@pushly/aqe/lib/components'
import { Form } from '@ant-design/compatible'
import '@ant-design/compatible/assets/index.css'
import { Input, Tooltip } from 'antd'
import { SimpleTimeframe } from '../../../../components/simple-timeframe/simple-timeframe'
import { InfoCircleOutlined } from '@ant-design/icons'
import {
    JOURNEY_ABANDONED_BROWSE_DEFAULT_ITEM_VIEWS,
    JOURNEY_ABANDONED_BROWSE_MAXIMUM_ITEM_VIEWS,
    JOURNEY_ABANDONED_BROWSE_MINIMUM_ITEM_VIEWS,
    JOURNEY_DEFAULT_ABANDONED_CART_COOLDOWN_PERIOD,
} from '../../../../components/journey-builder/configuration-editor/constants'
import './abandonment-settings-section.scss'
import { RelativeDateDisplayMetric } from '@pushly/aqe/lib/enums'
import { CatalogItemType } from '@pushly/models/lib/enums'
import {
    getPluralForCatalogItemType,
    getSingularForCatalogItemType,
} from '@pushly/models/lib/enums/catalogs/catalog-item-type'
import type { JourneyConfiguration } from '../../types/journey-api-response'
import { titleCase } from '../../../../_utils/utils'
import { TriggerType } from '@pushly/models/lib/enums/journeys'
import type { ITriggerStep } from '@pushly/models/lib/structs/journeys/steps/step-interfaces'
import { JOURNEY_FIELD_DESCRIPTIONS } from '../../field-descriptions'

export interface IAbandonmentSettingsSectionProps {
    showAbandonment: boolean
    showCooldown?: boolean
    showMinimumItemViews?: boolean
    showMaxItemAge: boolean

    itemType: CatalogItemType
    triggerType: TriggerType | undefined

    // Models
    configuration: JourneyConfiguration
    triggerConfig: ITriggerStep | undefined
    defaultMaxItemAgeDays?: number

    // Single mutator apply pattern: caller clones journey and applies
    onApply: (apply: (cfg: JourneyConfiguration, trig: ITriggerStep | undefined) => void) => void
}

const TitleWithInfo: React.FC<{ title: string; tooltip: string }> = ({ title, tooltip }) => (
    <div className="well-title-with-info" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span>{title}</span>
        <Tooltip title={tooltip}>
            <InfoCircleOutlined className="info-icon" />
        </Tooltip>
    </div>
)

const AbandonmentSettingsSectionCmp: React.FC<IAbandonmentSettingsSectionProps> = ({
    showAbandonment,
    showCooldown,
    showMinimumItemViews,
    showMaxItemAge,
    itemType,
    triggerType,
    configuration,
    triggerConfig,
    onApply,
    defaultMaxItemAgeDays,
}) => {
    const itemTypePlural = getPluralForCatalogItemType(itemType)
    const itemTypeSingular = getSingularForCatalogItemType(itemType)

    let maxItemAgeActionString = ''
    switch (triggerType) {
        case TriggerType.ABANDONED_CART:
            maxItemAgeActionString = 'carted'
            break
        case TriggerType.ABANDONED_VIEW:
            maxItemAgeActionString = 'viewed'
            break
        case TriggerType.SAVED_ITEM:
            maxItemAgeActionString = 'saved'
            break
    }

    return (
        <>
            {showAbandonment && (
                <Well
                    mode="ghost"
                    title={
                        <TitleWithInfo
                            title="Abandonment timeframe"
                            tooltip={JOURNEY_FIELD_DESCRIPTIONS.abandonmentTimeframe(itemTypeSingular)}
                        />
                    }
                    hideFooter
                    className="entry-cap-well"
                >
                    <div className="rcte-body-compact">
                        <Form.Item className="journey-trigger-editor-label" colon={false} style={{ margin: 0 }}>
                            <div className="rcte-inline-sentence">
                                <span>Consider the subscriber abandoned after</span>
                                <span className="rcte-control">
                                    <SimpleTimeframe
                                        size="small"
                                        disabledMetrics={['seconds']}
                                        value={{
                                            seconds: configuration?.abandonment_seconds ?? 3600,
                                            metric: configuration?.abandonment_metric ?? 'minutes',
                                        }}
                                        onChange={(v) =>
                                            onApply((cfg) => {
                                                cfg.abandonment_seconds = v?.seconds
                                                cfg.abandonment_metric = v?.metric
                                            })
                                        }
                                    />
                                </span>
                                <span>of inactivity.</span>
                            </div>
                        </Form.Item>
                    </div>
                </Well>
            )}

            {showCooldown && (
                <Well
                    mode="ghost"
                    title={
                        <TitleWithInfo
                            title="Browse abandonment eligibility"
                            tooltip={JOURNEY_FIELD_DESCRIPTIONS.browseAbandonmentEligibility}
                        />
                    }
                    hideFooter
                    className="entry-cap-well"
                >
                    <div className="rcte-body-compact">
                        <Form.Item className="journey-trigger-editor-label" colon={false} style={{ margin: 0 }}>
                            <div className="rcte-inline-sentence">
                                <span>After completing a cart abandonment journey, wait at least</span>
                                <span className="rcte-control">
                                    <SimpleTimeframe
                                        size="small"
                                        disabledMetrics={['seconds', 'minutes', 'hours']}
                                        value={{
                                            seconds:
                                                configuration?.abandoned_cart_cooldown_period?.interval_seconds ??
                                                JOURNEY_DEFAULT_ABANDONED_CART_COOLDOWN_PERIOD.interval_seconds,
                                            metric: RelativeDateDisplayMetric.DAYS,
                                        }}
                                        onChange={(v) =>
                                            onApply((cfg) => {
                                                cfg.abandoned_cart_cooldown_period = {
                                                    interval_seconds: v?.seconds,
                                                    display_metric: cfg.abandoned_cart_cooldown_period?.display_metric,
                                                }
                                            })
                                        }
                                    />
                                </span>
                                <span>before allowing entry into this browse abandonment journey.</span>
                            </div>
                        </Form.Item>
                    </div>
                </Well>
            )}

            {showMinimumItemViews && (
                <Well
                    mode="ghost"
                    title={
                        <TitleWithInfo
                            title={`Minimum ${itemTypePlural} views to qualify`}
                            tooltip={JOURNEY_FIELD_DESCRIPTIONS.minimumItemViews(itemTypeSingular)}
                        />
                    }
                    hideFooter
                    className="entry-cap-well"
                >
                    <div className="rcte-body-compact">
                        <Form.Item className="journey-trigger-editor-label" colon={false} style={{ margin: 0 }}>
                            <div className="rcte-inline-sentence">
                                <span>Require a {itemTypeSingular} to be viewed at least</span>
                                <span className="rcte-control">
                                    <Input
                                        aria-label={`Required ${itemTypePlural} view count`}
                                        type="number"
                                        size="small"
                                        min={JOURNEY_ABANDONED_BROWSE_MINIMUM_ITEM_VIEWS}
                                        max={JOURNEY_ABANDONED_BROWSE_MAXIMUM_ITEM_VIEWS}
                                        placeholder={`${JOURNEY_ABANDONED_BROWSE_MINIMUM_ITEM_VIEWS}–${JOURNEY_ABANDONED_BROWSE_MAXIMUM_ITEM_VIEWS}`}
                                        defaultValue={JOURNEY_ABANDONED_BROWSE_DEFAULT_ITEM_VIEWS}
                                        value={configuration?.abandoned_browse_minimum_item_views}
                                        onChange={(e) =>
                                            onApply((cfg) => {
                                                const parsed = Number.parseInt(e?.target?.value, 10)
                                                cfg.abandoned_browse_minimum_item_views =
                                                    Number.isNaN(parsed) ? undefined : parsed
                                            })
                                        }
                                    />
                                </span>
                                <span>time(s) to be eligible for this journey.</span>
                            </div>
                        </Form.Item>
                    </div>
                </Well>
            )}

            {showMaxItemAge && (
                <Well
                    mode="ghost"
                    title={
                        <TitleWithInfo
                            title={`Maximum ${maxItemAgeActionString} ${itemTypePlural} age`}
                            tooltip={JOURNEY_FIELD_DESCRIPTIONS.maximumItemAge(titleCase(maxItemAgeActionString), itemTypePlural)}
                        />
                    }
                    hideFooter
                    className="entry-cap-well"
                >
                    <div className="rcte-body-compact">
                        <Form.Item className="journey-trigger-editor-label" colon={false} style={{ margin: 0 }}>
                            <div className="rcte-inline-sentence">
                                <span>Exclude {itemTypePlural} that were {maxItemAgeActionString} more than </span>
                                <span className="rcte-control">
                                    <SimpleTimeframe
                                        size="small"
                                        disabledMetrics={['seconds', 'minutes', 'hours']}
                                        value={{
                                            seconds: (() => {
                                                const days = triggerConfig?.configuration?.params?.max_item_age_days
                                                    ?? defaultMaxItemAgeDays ?? 0
                                                return days * 24 * 60 * 60
                                            })(),
                                            metric: RelativeDateDisplayMetric.DAYS,
                                        }}
                                        onChange={(v) =>
                                            onApply((cfg, trig) => {
                                                const currentParams = trig?.configuration?.params ?? {}
                                                const days = (v?.seconds ?? 0) / (24 * 60 * 60)
                                                if (trig) {
                                                    Object.assign(trig.configuration, {
                                                        params: {
                                                            ...currentParams,
                                                            max_item_age_days: Number.isFinite(days) ? Math.floor(days) : 0,
                                                        },
                                                    })
                                                }
                                            })
                                        }
                                    />
                                </span>
                                <span> ago.</span>
                            </div>
                        </Form.Item>
                    </div>
                </Well>
            )}
        </>
    )
}

export default React.memo(AbandonmentSettingsSectionCmp)
